import { useState, useEffect, useCallback, useRef } from 'react';

const DISMISS_KEY = 'pwaInstallBannerDismissedAt';

/** Matches manifest.json start_url — in-scope link opens installed PWA on Chrome Android */
export const PWA_START_URL = '/picking';

/**
 * Open the installed picking PWA from a browser tab.
 * Chrome Android intercepts an in-scope target=_blank navigation and launches the
 * standalone app when getInstalledRelatedApps reports it is installed.
 */
export function openInstalledPwa(startUrl = PWA_START_URL) {
  if (typeof window === 'undefined') return;
  const url = new URL(startUrl, window.location.origin).href;
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Chrome fires beforeinstallprompt once, early at page load — usually before
// login completes and the picking view (and this hook) mounts. Capture it at
// module load so the banner can use it whenever it renders.
let deferredInstallEvent = null;
const installEventSubscribers = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppress Chrome's mini-infobar; we show our own banner.
    event.preventDefault();
    deferredInstallEvent = event;
    installEventSubscribers.forEach((notify) => notify(event));
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallEvent = null;
    installEventSubscribers.forEach((notify) => notify(null));
  });
}

export function isStandaloneDisplayMode() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari home-screen apps
  if (window.navigator.standalone === true) return true;
  return false;
}

export function isIosBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIosDevice = /iPad|iPhone|iPod/.test(ua)
    // iPadOS 13+ reports as Mac with touch support
    || (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1);
  return isIosDevice;
}

function wasDismissedThisSession() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) !== null;
  } catch (_) {
    return false;
  }
}

function hasEverDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY) !== null;
  } catch (_) {
    return false;
  }
}

/**
 * PWA install state for the picking mobile app.
 *
 * platform:
 *  - 'android' — beforeinstallprompt captured; promptInstall() fires the native sheet
 *  - 'ios'     — no install API; UI must show Add to Home Screen instructions
 *  - null      — not installable here (desktop, already standalone, or prompt not fired yet)
 *
 * Dismissal: hiding the banner lasts for the browser session; on the next
 * session it returns in compact form (dismissedBefore === true).
 */
export function usePwaInstall() {
  const [standalone, setStandalone] = useState(() => isStandaloneDisplayMode());
  const [installEvent, setInstallEvent] = useState(() => deferredInstallEvent);
  const [installed, setInstalled] = useState(false);
  const [alreadyInstalled, setAlreadyInstalled] = useState(false);
  const [dismissedThisSession, setDismissedThisSession] = useState(() => wasDismissedThisSession());
  const dismissedBeforeRef = useRef(hasEverDismissed());

  // Best-effort: Chrome Android can report our own PWA as installed via
  // related_applications (platform "webapp") in the manifest.
  useEffect(() => {
    if (standalone || typeof navigator === 'undefined' || !navigator.getInstalledRelatedApps) return;
    navigator.getInstalledRelatedApps()
      .then((apps) => {
        if (Array.isArray(apps) && apps.length > 0) setAlreadyInstalled(true);
      })
      .catch(() => {});
  }, [standalone]);

  useEffect(() => {
    const onInstallEventChange = (event) => {
      setInstallEvent(event);
      if (event === null) setInstalled(true);
    };
    installEventSubscribers.add(onInstallEventChange);
    // Event may have fired between initial state and this effect
    if (deferredInstallEvent) setInstallEvent(deferredInstallEvent);

    const displayModeQuery = window.matchMedia ? window.matchMedia('(display-mode: standalone)') : null;
    const onDisplayModeChange = () => setStandalone(isStandaloneDisplayMode());
    displayModeQuery?.addEventListener?.('change', onDisplayModeChange);

    return () => {
      installEventSubscribers.delete(onInstallEventChange);
      displayModeQuery?.removeEventListener?.('change', onDisplayModeChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installEvent) return false;
    try {
      installEvent.prompt();
      const choice = await installEvent.userChoice;
      deferredInstallEvent = null;
      setInstallEvent(null);
      if (choice?.outcome === 'accepted') {
        setInstalled(true);
        return true;
      }
      return false;
    } catch (_) {
      deferredInstallEvent = null;
      setInstallEvent(null);
      return false;
    }
  }, [installEvent]);

  const dismiss = useCallback(() => {
    const now = String(Date.now());
    try {
      sessionStorage.setItem(DISMISS_KEY, now);
      localStorage.setItem(DISMISS_KEY, now);
    } catch (_) {}
    dismissedBeforeRef.current = true;
    setDismissedThisSession(true);
  }, []);

  const openInstalledApp = useCallback(() => {
    openInstalledPwa();
  }, []);

  const ios = isIosBrowser();
  let platform = null;
  if (!standalone && !installed) {
    if (installEvent) platform = 'android';
    else if (ios) platform = 'ios';
  }

  return {
    /** Running inside the installed full-screen app */
    standalone,
    /** 'android' | 'ios' | null — how install can happen from this browser */
    platform,
    /** Installed during this session (or native prompt accepted) */
    installed,
    /** App already installed on this device but running in the browser tab (Android best-effort) */
    alreadyInstalled: alreadyInstalled || installed,
    /** Fire the native Android/Chrome install sheet */
    promptInstall,
    /** Launch the installed standalone app (Chrome Android; no-op elsewhere) */
    openInstalledApp,
    /** Hide the banner for this session */
    dismiss,
    dismissedThisSession,
    /** User dismissed in a previous session — show compact pill instead of full banner */
    dismissedBefore: dismissedBeforeRef.current,
  };
}
